import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search, Users } from "lucide-react";
import { fetchUsers } from "@/lib/queries";
import { ago, date, number } from "@/lib/format";

const PAGE_SIZE = 25;

export function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search);
      setPage(0);
    }, 280);
    return () => clearTimeout(timer);
  }, [search]);

  const users = useQuery({
    queryKey: ["users", debounced, page],
    queryFn: () => fetchUsers(debounced, page, PAGE_SIZE),
    placeholderData: (previous) => previous,
  });

  const total = users.data?.total ?? 0;
  const rows = users.data?.rows ?? [];
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ fontSize: 15 }}>Everyone who signed up</h2>
          <p>Pseudonymous by default. Open a row for consent, study history and devices.</p>
        </div>
        <div className="search">
          <Search size={14} />
          <input
            className="input"
            placeholder="Search name or id…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Users</h2>
            <p>{number(total)} matching accounts</p>
          </div>
          <div className="spacer" />
          <span className="badge badge-sky">
            <Users size={12} />
            {number(rows.length)} on this page
          </span>
        </div>

        <div className="table-wrap" style={{ marginTop: 14 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Learner</th>
                <th>Plan</th>
                <th>Streak</th>
                <th>Reviews · 30d</th>
                <th>Last active</th>
                <th>Joined</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.isLoading && (
                <tr>
                  <td colSpan={7} className="empty">
                    <div className="spinner" />
                  </td>
                </tr>
              )}
              {!users.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty">
                    No users match “{debounced}”.
                  </td>
                </tr>
              )}
              {rows.map((user) => (
                <tr
                  key={user.id}
                  className="clickable"
                  onClick={() => navigate(`/users/${user.id}`)}
                >
                  <td>
                    <div className="row">
                      <div className={`avatar ${user.plan === "pro" ? "avatar-coral" : "avatar-mint"}`}>
                        {user.display_name.slice(0, 1).toUpperCase()}
                      </div>
                      <div>
                        <div className="cell-strong">{user.display_name}</div>
                        <div className="cell-sub mono">{user.id.slice(0, 8)}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${user.plan === "pro" ? "badge-coral" : ""}`}>
                      {user.plan}
                    </span>
                    {user.is_deleted && <span className="badge badge-danger" style={{ marginLeft: 4 }}>deleting</span>}
                  </td>
                  <td>
                    <span className="cell-strong">{user.streak_current}</span>
                    <span className="faint small"> days</span>
                  </td>
                  <td>{number(user.reviews_30d)}</td>
                  <td className="nowrap muted">{ago(user.last_active)}</td>
                  <td className="nowrap faint">{date(user.created_at)}</td>
                  <td className="right">
                    <ChevronRight size={15} className="faint" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="row-between" style={{ padding: "12px 18px", borderTop: "1px solid var(--line)" }}>
          <span className="small faint">
            Page {page + 1} of {lastPage + 1}
          </span>
          <div className="row" style={{ gap: 6 }}>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page === 0}
              onClick={() => setPage((value) => Math.max(0, value - 1))}
            >
              <ChevronLeft size={13} />
              Previous
            </button>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page >= lastPage}
              onClick={() => setPage((value) => Math.min(lastPage, value + 1))}
            >
              Next
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
